import { Reveal, MaskedLine } from "@/components/Motion";
import BookButton from "@/components/BookButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/i18n";
import { SEO } from "@/components/SEO";

const PageHeader = () => {
  const { t } = useLang();
  return (
    <section data-testid="faq-header" className="bg-alabaster pb-16 pt-40">
      <SEO
        title="FAQ | VCK Business Lab"
        description="Answers about VCK Business Lab's virtual business support services, packages, pricing, languages, and how we work."
        path="/faq"
      />
      <div className="mx-auto max-w-4xl px-6 sm:px-12">
        <MaskedLine delay={0.1}>
          <span className="overline text-gold-muted">{t("faqPage.overline")}</span>
        </MaskedLine>
        <h1 className="mt-6 font-serif text-4xl font-light leading-[1.1] tracking-tight text-obsidian sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>{t("faqPage.line1")}</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold-muted">{t("faqPage.line2")}</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <span className="mt-8 block max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            {t("faqPage.intro")}
          </span>
        </MaskedLine>
      </div>
    </section>
  );
};

const FaqList = () => {
  const { c } = useLang();
  return (
    <section className="bg-alabaster pb-24">
      <div className="mx-auto max-w-4xl px-6 sm:px-12">
        <Reveal>
          <Accordion type="single" collapsible className="w-full">
            {c.faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i + 1}`}
                className="border-obsidian/10"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i + 1}`}
                  className="gap-4 text-left hover:text-gold-muted hover:no-underline"
                >
                  <span className="flex items-baseline gap-4">
                    <span className="font-mono text-xs tracking-[0.2em] text-gold-muted">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-lg text-obsidian sm:text-xl">{faq.q}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="ps-9 text-sm leading-relaxed text-slate-600 sm:ps-10">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

const ContactCallout = () => {
  const { t } = useLang();
  return (
    <section className="bg-alabaster pb-24 lg:pb-32">
      <div className="mx-auto max-w-4xl px-6 sm:px-12">
        <Reveal>
          <div
            data-testid="faq-contact-callout"
            className="relative overflow-hidden rounded-2xl bg-obsidian p-10 text-center sm:p-14"
          >
            <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[480px] -translate-x-1/2 rounded-full bg-gold/10 blur-[100px]" />
            <div className="relative">
              <p className="overline text-gold">{t("faqPage.calloutOverline")}</p>
              <h2 className="mt-5 font-serif text-3xl leading-tight text-white sm:text-4xl">
                {t("faqPage.calloutTitle")}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400">{t("faqPage.calloutText")}</p>
              <div className="mt-8 flex justify-center">
                <BookButton testid="faq-book-consultation-button">{t("faqPage.calloutBtn")}</BookButton>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Faq() {
  return (
    <div data-testid="faq-page">
      <PageHeader />
      <FaqList />
      <ContactCallout />
    </div>
  );
}
